<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="documentStatuses"
      :headers="tableHeaders"
      :list="documentStatuses"
      table-id="document-statuses"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="refreshData"
      clickable
      @table-item-clicked="onEdit"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as DocumentStatus)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <DocumentStatusModalCreateEdit
      v-if="createEditModalActive"
      :document-status="documentStatus"
      :title="modalEditCreateTitle()"
      :mode="createEditModalMode"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { removeDocumentStatusesByIds, getDocumentStatuses } from '@/services/api/documents';

import BaseTable from '@/components/common/BaseTable.vue';
import DocumentStatusModalCreateEdit from '@/components/documents/DocumentStatusModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type DocumentStatus, eDocumentStatus } from '@/types/DocumentStatus';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';

const { reloadLookups } = useLookupsStore();
const { t: $t } = useI18n({ useScope: 'global' });
const documentStatuses = ref<DocumentStatus[]>();
const filterIsActive = ref<boolean | undefined>(undefined);

const tableHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'statusString', text: $t('pricing-catalogs.status'), type: 'text', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initializeDocumentStatus(): DocumentStatus {
  return {
    id: uuidv4(),
    code: '',
    name: '',
    status: null,
    statusString: '',
    isFromManager: true,
    isActive: false,
  };
}

onMounted(async () => {
  const res = await getDocumentStatuses();
  documentStatuses.value = populateDocumentStatusField(res);
});

function getDocumentStatusString(status: number): string {
  switch (status) {
    case eDocumentStatus.Open:
      return 'Open';
    case eDocumentStatus.InProgress:
      return 'In Progress';
    case eDocumentStatus.Hold:
      return 'Hold';
    case eDocumentStatus.Closed:
      return 'Closed';
    default:
      return 'Unknown';
  }
}

function populateDocumentStatusField(documentStatuses: DocumentStatus[]): DocumentStatus[] {
  return documentStatuses.map((status) => ({
    ...status,
    statusString: status.status != null ? getDocumentStatusString(status.status) : undefined,
  }));
}

const toastStore = useToastStore();

async function onDeleteMultiselect(items: DocumentStatus[]) {
  const itemsArray = Array.isArray(items) ? items : [items];
  const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.info({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }

  const idsToDelete = itemsToDelete.map((item) => item.id);
  const res = await removeDocumentStatusesByIds(idsToDelete);
  if (!res && documentStatuses.value)
    documentStatuses.value = documentStatuses.value.filter(
      (status) => !idsToDelete.includes(status.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['documentStatuses']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeDocumentStatusesByIds([idToDelete]);
  if (!res && documentStatuses.value)
    documentStatuses.value = documentStatuses.value.filter((status) => status.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['documentStatuses']);
}

const documentStatus = ref<DocumentStatus>(initializeDocumentStatus());

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function onAddNew() {
  documentStatus.value = initializeDocumentStatus();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEdit(item: DocumentStatus) {
  documentStatus.value = item;
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

async function onSave(modifiedData: DocumentStatus) {
  if (!documentStatuses.value) return;

  const index = documentStatuses.value.findIndex((item) => item.id === modifiedData.id);

  if (index !== -1) documentStatuses.value[index] = modifiedData;
  else documentStatuses.value.push(modifiedData);
  createEditModalActive.value = false;

  await reloadLookups(['documentStatuses']);
}

async function refreshData(data: TableFilterData) {
  const response = await getDocumentStatuses();
  let processedData = populateDocumentStatusField(response);

  if (data.query) {
    processedData = filterData(processedData, data.query, ['name', 'code', 'statusString']);
  }

  if (data.column !== undefined && data.order) {
    processedData = sortData(processedData, data.column, data.order, tableHeaders);
  }

  if (data.isActive !== undefined) processedData = filterActive(processedData, data.isActive);

  documentStatuses.value = processedData;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DocumentStatus>();
function onDeleteRow(item: DocumentStatus) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (documentStatus.value && !documentStatus.value.isFromManager)
    return `${$t('common.actions.view')} Document Status`;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} Document Status`
    : `${$t('common.actions.create')} Document Status`;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
@/components/documents/DocumentStatusModalCreateEdit.vue
